import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  UserCredential
} from 'firebase/auth'

import db from '../db/db'
import initFirebase from '../db/initFirebase'
import { AnalyticsService } from '../services/AnalyticsService'
import { AnalyticsEvents } from '../types/AnalyticsEvents'

export type OAuthProviderType = 'google' | 'microsoft'

interface OAuthSignInResult {
  success: boolean
  userCredential?: UserCredential
  error?: string
  isNewUser?: boolean
}

/**
 * Sign in with OAuth provider (Google or Microsoft)
 */
export const signInWithOAuth = async (
  providerType: OAuthProviderType
): Promise<OAuthSignInResult> => {
  try {
    const auth = initFirebase.getFireBaseStore().auth
    let provider

    if (providerType === 'google') {
      provider = new GoogleAuthProvider()
      provider.addScope('profile')
      provider.addScope('email')
    } else if (providerType === 'microsoft') {
      provider = new OAuthProvider('microsoft.com')
      provider.addScope('email')
      provider.addScope('profile')
    } else {
      return {
        success: false,
        error: 'Invalid provider type'
      }
    }

    const result = await signInWithPopup(auth, provider)
    const isNewUser =
      result.user.metadata.creationTime === result.user.metadata.lastSignInTime

    return {
      success: true,
      userCredential: result,
      isNewUser
    }
  } catch (error: any) {
    console.error(`OAuth sign-in error (${providerType}):`, error)

    let errorMessage = 'Authentication failed. Please try again.'

    if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'Sign-in cancelled'
    } else if (error.code === 'auth/popup-blocked') {
      errorMessage = 'Pop-up blocked. Please allow pop-ups for this site.'
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      errorMessage =
        'An account already exists with this email using a different sign-in method.'
    } else if (error.code === 'auth/cancelled-popup-request') {
      errorMessage = 'Sign-in cancelled'
    }

    return {
      success: false,
      error: errorMessage
    }
  }
}

/**
 * Process user creation for OAuth sign-ins
 */
export const processOAuthUserCreation = async (
  userCredential: UserCredential,
  theme: string = 'light'
): Promise<{ officeIds: string[]; rejectedOffices: string[] }> => {
  const user = userCredential.user
  const email = user.email?.toLowerCase() || ''
  const officeIdsToAdd: string[] = []
  const rejectedOffices: string[] = []

  try {
    // Check if user already exists in database
    const existingUser = await db.getById('users', user.uid)
    if (existingUser) {
      console.log('User already exists in database')
      return { officeIds: existingUser.officeids || [], rejectedOffices: [] }
    }

    // Process pending invites
    const pendingInvites = await db.query({
      collection: 'invite',
      where: ['email', '==', email]
    })

    console.log(`Found ${pendingInvites.length} pending invites for ${email}`)

    // Check capacity for each invited office
    const { canAddUser } = await import('../services/subscriptionService')

    for (const invite of pendingInvites) {
      if (invite.oid) {
        try {
          const hasCapacity = await canAddUser(invite.oid)

          if (hasCapacity) {
            officeIdsToAdd.push(invite.oid)
            console.log(`Adding user to office ${invite.oid}`)

            // Track office invitation accepted event
            AnalyticsService.trackEvent(AnalyticsEvents.INVITE_ACCEPTED, {
              invitation_type: 'office'
            })
          } else {
            const office = await db.getById('offices', invite.oid)
            rejectedOffices.push(office?.name || invite.oid)
            console.log(`Office ${invite.oid} is at capacity, skipping`)
          }

          if (invite.id) {
            await db.deleteItem('invite', invite.id)
          }
        } catch (err) {
          console.error(
            `Error processing invite for office ${invite.oid}:`,
            err
          )
        }
      }
    }

    // Update pending office manager records
    const pendingManagerRecords = await db.query({
      collection: 'officeManagers',
      where: ['email', '==', email]
    })

    for (const record of pendingManagerRecords) {
      if (
        record.id &&
        record.officeId &&
        (!record.userId || record.userId === null)
      ) {
        if (!officeIdsToAdd.includes(record.officeId)) {
          await db.deleteItem('officeManagers', record.id)
          console.log(
            `Deleted pending office manager record for office ${record.officeId}`
          )
          continue
        }

        const newId = `${record.officeId}_${user.uid}`
        const newManagerData = {
          officeId: record.officeId,
          userId: user.uid,
          email: email,
          addedBy: record.addedBy || user.uid,
          addedOnDate: record.addedOnDate || new Date()
        }

        const { getFirestore, doc, setDoc } = await import('firebase/firestore')
        const firestore = getFirestore()
        await setDoc(doc(firestore, 'officeManagers', newId), newManagerData)
        await db.deleteItem('officeManagers', record.id)
        console.log(
          `Updated office manager record for office ${record.officeId}`
        )
      }
    }

    // Extract name from OAuth profile
    const displayName = user.displayName || ''
    const nameParts = displayName.split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    // Create user record with Auth UID as document ID
    await db.addItem('users', {
      id: user.uid,
      email: user.email,
      uid: user.uid,
      firstname: firstName,
      lastname: lastName,
      marketing: false,
      officeids: officeIdsToAdd,
      theme: theme,
      isAdmin: false,
      createdAt: new Date()
    } as any)

    console.log(`Created user with ${officeIdsToAdd.length} offices`)

    return { officeIds: officeIdsToAdd, rejectedOffices }
  } catch (err) {
    console.error('Error processing OAuth user creation:', err)
    throw err
  }
}
